import { useState, type FormEvent } from 'react';
import { activateInvitation } from '../api';
import { useAuth } from './AuthContext';

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Invite activation surface (Story 1.5). Reached via the magic link `?token=…`.
 * The invitee sets a password; on success the returned session is adopted and
 * the app lands in the right role shell. A used/expired token shows the refusal.
 */
export function Activate({
  token,
  onDone,
}: {
  token: string;
  onDone: () => void;
}): React.JSX.Element {
  const { adoptSession } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    // The form is noValidate, so enforce the min-length here rather than only
    // relying on the server to reject a short/empty password.
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const session = await activateInvitation({ token, password });
      adoptSession(session);
      onDone(); // clear ?token= and hand off to the role shell
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Activation failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--app-bg)',
        padding: 'var(--gutter)',
      }}
    >
      <section
        className="card stack"
        aria-labelledby="activate-heading"
        style={{ width: 'min(400px, 100%)' }}
      >
        <div className="stack" style={{ gap: 4 }}>
          <h1 id="activate-heading" style={{ margin: 0, fontSize: 21, fontWeight: 800 }}>
            Set your password
          </h1>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Finish onboarding by choosing a password for your account.
          </p>
        </div>

        <form className="stack" onSubmit={onSubmit} noValidate>
          <div className="stack" style={{ gap: 6 }}>
            <label className="field-label" htmlFor="new-password">
              Password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={fieldStyle}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              At least 8 characters.
            </span>
          </div>

          <div role="alert" aria-live="assertive" style={{ minHeight: 18 }}>
            {error && (
              <span className="status-pill" data-tone="bad">
                <span className="status-pill__dot" aria-hidden="true" />
                {error}
              </span>
            )}
          </div>

          <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
            {submitting ? 'Activating…' : 'Activate account'}
          </button>
        </form>
      </section>
    </div>
  );
}
